import {
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { TruncatedText } from '@/components/commons/truncated-text'
import { DataTable } from '@/components/ui/data-table'
import { DropdownContent, DropdownItem } from '@/components/ui/dropdown-atoms'
import { DotsHorizontalIcon } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { AttendeeNameLink } from '@/features/marquee/components/attendee-name-link'
import { CheckInRowSheet } from '@/features/marquee/components/check-in-row-sheet'
import { RsvpStatusCell } from '@/features/marquee/components/rsvp-status-cell'
import { TableScrollBox } from '@/features/marquee/components/table-scroll-box'
import {
	TableEmpty,
	TableError
} from '@/features/marquee/components/table-states'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import {
	attendeeDetailPath,
	type CheckInAction,
	type CheckInActionId,
	checkInActionGates,
	checkInActionToLabelKey,
	childAccountDisplay,
	hasNoSalesforceAccount,
	isMarketManagerAllowed,
	segmentLabel
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type {
	MarqueeAttendee,
	MarqueeCaptureMode,
	MarqueeEventState
} from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

/**
 * The two fixes a row with no Salesforce account is offered, in the check-in
 * matrix's own vocabulary so both rosters label and tone them identically.
 */
const ROW_FIX_ACTIONS: CheckInAction[] = [
	{ id: 'resolve_id_capture', tone: 'default' },
	{ id: 'mark_unresolvable', tone: 'danger' }
]

/**
 * What a row marked unresolvable offers instead: walking the mark back, and
 * nothing else. Same precedence as the check-in matrix's first branch: the
 * operator's give-up verdict replaces the fixes rather than sitting beside them,
 * because offering "mark unresolvable" on an already-marked row is a write that
 * changes nothing.
 */
const ROW_RESTORE_ACTIONS: CheckInAction[] = [
	{ id: 'restore_resolution', tone: 'default' }
]

/**
 * The row's resolution actions. `is_unresolvable` wins: it is a verdict about
 * the row, so until it is walked back nothing else about the row is actionable.
 */
const rowResolutionActions = (attendee: MarqueeAttendee): CheckInAction[] =>
	attendee.is_unresolvable ? ROW_RESTORE_ACTIONS : ROW_FIX_ACTIONS

/**
 * Does this row get a menu at all?
 *
 * Two populations, for two different reasons. A row with no Salesforce account
 * is work waiting: it cannot export until someone fixes or writes it off. A row
 * already written off carries no work, but it must stay reachable. The verdict
 * was a judgement call, often made at the desk mid-event, and the operator who
 * changes their mind has to be able to say so here. Walk-ins reach the menu
 * through the second test only: they are born without an account (so the first
 * test skips them by design), but a walk-in written off is exactly as
 * recoverable as an invitee written off.
 */
const hasRowActions = (attendee: MarqueeAttendee): boolean =>
	attendee.is_unresolvable === true || hasNoSalesforceAccount(attendee)

/**
 * What an empty account / host-ID cell says.
 *
 * Normally "Missing", the gap to go and fix. On a row the operator has given up
 * on it reads "Unresolvable" instead: the cell is empty for the same reason, but
 * it is no longer work waiting for anyone, and that verdict has nowhere else to
 * show on this table. The other resolution statuses need no cell of their own,
 * being exactly what these two columns already show.
 */
const missingCellKey = (attendee: MarqueeAttendee): TKey =>
	attendee.is_unresolvable
		? 'marquee:attendee_status_unresolvable'
		: 'marquee:attendee_missing'

/** Tone → menu-item color, so kebab and sheet paint an action identically. */
const ACTION_TONE_CLASS = {
	default: 'text-neutral-900',
	danger: 'text-error-600',
	ok: 'text-neutral-900'
} as const satisfies Record<CheckInAction['tone'], string>

interface AttendeesTableProps {
	data: MarqueeAttendee[]
	/** Event id — enables navigating a row to its attendee-detail page. */
	eventId?: string
	/** The event's capture mode: only a tune-up has an attendee-detail page. */
	captureMode?: MarqueeCaptureMode
	/** Lifecycle state: the RSVP is correctable in `pre_work` and nowhere else. */
	eventState?: MarqueeEventState
	/**
	 * Opens the assign-host dialog for a row with no Salesforce account — the fix
	 * for exactly the rows the lock's completeness warning counts. Omitted (or
	 * refused by the gate below) leaves those rows with no action, as before.
	 */
	onAssignHost?: (attendee: MarqueeAttendee) => void
	/** Opens the give-up confirmation for the same rows. */
	onMarkUnresolvable?: (attendee: MarqueeAttendee) => void
	/**
	 * Walks back a give-up verdict, the only action a marked row offers. Writes
	 * straight from the row (no dialog): the mark is what needed confirming, and
	 * undoing it puts the row back where it already was.
	 */
	onRestoreResolution?: (attendee: MarqueeAttendee) => void
	/** Attendee id whose restore is in flight, so the spinner lands on its row. */
	restoringAttendeeId?: string | null
	isLoading?: boolean
	isError?: boolean
}

export function AttendeesTable({
	data,
	eventId,
	captureMode,
	eventState,
	onAssignHost,
	onMarkUnresolvable,
	onRestoreResolution,
	restoringAttendeeId,
	isLoading,
	isError
}: AttendeesTableProps) {
	const isPreWork = eventState === 'pre_work'
	// Correcting an invite answer is pre-work data entry, which the partition
	// files under setup rather than door work. Asked once for the whole table:
	// the cells share one cached answer, so the column simply stops being
	// editable for a facilitator.
	const { data: operator } = useQuery(operatorQueryOptions())
	const isAllowedToSetRsvp = isMarketManagerAllowed(
		operator,
		'setAttendeeRsvp'
	)
	const isRsvpEditable = isPreWork && isAllowedToSetRsvp
	// The resolution writes answer in every state but `locked` — the one frozen
	// state, where the backend returns 409 `invalid-state`. Offering the action
	// there would hand the operator a button that only errors.
	//
	// Deliberately wider than the post-lock banner above the table, which starts
	// at `locked`: in `pre_work` a missing account is at its cheapest to fix, and
	// gating the fix on the warning would read as "you may only correct this once
	// it is too late to correct it cheaply". So the row offers the fix wherever
	// the backend accepts it, and the banner is what makes it urgent.
	// A caller that wires neither handler gets no menu; either one is enough,
	// since the two populations the menu serves are disjoint (a row still in play
	// needs the fixes, a row written off needs only the restore).
	const canResolve =
		(onAssignHost !== undefined || onRestoreResolution !== undefined) &&
		checkInActionGates(eventState).resolution
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')

	const columns = useMemo(
		() => [
			{
				id: 'attendee' as const,
				header: t('marquee:attendee_col_attendee'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<div className='flex min-w-0 flex-col'>
						<AttendeeNameLink
							className='font-medium text-neutral-900 text-sm'
							name={dataItem.name || '-'}
							to={attendeeDetailPath(
								eventId,
								dataItem.id,
								captureMode
							)}
						/>
						{dataItem.company && (
							<TruncatedText className='text-neutral-500 text-sm'>
								{dataItem.company}
							</TruncatedText>
						)}
					</div>
				),
				size: 240
			},
			{
				id: 'childAccount' as const,
				header: t('marquee:attendee_col_child_account'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => {
					const { value, isParent } = childAccountDisplay(dataItem)
					return value ? (
						<div className='flex min-w-0 flex-col'>
							<TruncatedText className='font-medium text-neutral-900 text-sm'>
								{value}
							</TruncatedText>
							{isParent && (
								<TruncatedText className='text-neutral-500 text-sm'>
									{t('marquee:attendee_child_parent')}
								</TruncatedText>
							)}
						</div>
					) : (
						<span className='text-neutral-500 text-sm'>
							{t(missingCellKey(dataItem))}
						</span>
					)
				},
				size: 210
			},
			{
				id: 'hostId' as const,
				header: t('marquee:attendee_col_host_id'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<TruncatedText
						className={
							dataItem.airbnb_host_id
								? 'font-medium text-neutral-900 text-sm'
								: 'text-neutral-500 text-sm'
						}
					>
						{dataItem.airbnb_host_id ?? t(missingCellKey(dataItem))}
					</TruncatedText>
				),
				size: 160
			},
			{
				id: 'segment' as const,
				header: t('marquee:attendee_col_segment'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<TruncatedText className='font-medium text-neutral-900 text-sm'>
						{segmentLabel(dataItem.segment)}
					</TruncatedText>
				),
				size: 160
			},
			{
				id: 'rsvp' as const,
				header: t('marquee:attendee_col_rsvp'),
				cell: ({ dataItem }: { dataItem: MarqueeAttendee }) => (
					<RsvpStatusCell
						attendee={dataItem}
						editable={isRsvpEditable}
						eventId={eventId}
					/>
				),
				size: 200
			},
			// Dropped entirely rather than left empty while the event is
			// `locked`: no row can offer a fix there, so the column would be a
			// pinned 160px of heading over nothing.
			...(canResolve
				? [
						{
							id: 'actions' as const,
							header: t('common:actions'),
							headerAlign: 'end' as const,
							cell: ({
								dataItem
							}: {
								dataItem: MarqueeAttendee
							}) => (
								<div className='flex w-full items-center justify-end'>
									<AttendeeRowMenu
										attendee={dataItem}
										canResolve={canResolve}
										detailPath={attendeeDetailPath(
											eventId,
											dataItem.id,
											captureMode
										)}
										isRestoring={
											restoringAttendeeId === dataItem.id
										}
										onAssignHost={onAssignHost}
										onMarkUnresolvable={onMarkUnresolvable}
										onRestoreResolution={
											onRestoreResolution
										}
									/>
								</div>
							),
							size: 160
						}
					]
				: [])
		],
		[
			t,
			eventId,
			captureMode,
			isRsvpEditable,
			canResolve,
			onAssignHost,
			onMarkUnresolvable,
			onRestoreResolution,
			restoringAttendeeId
		]
	)

	// Mobile: the table collapses into a stack of cards (same style as the
	// events list) that flow with the page instead of scrolling at a fixed
	// height.
	if (!isDesktop) {
		return (
			<AttendeesCards
				canResolve={canResolve}
				captureMode={captureMode}
				data={data}
				eventId={eventId}
				isError={isError}
				isLoading={isLoading}
				isRsvpEditable={isRsvpEditable}
				onAssignHost={onAssignHost}
				onMarkUnresolvable={onMarkUnresolvable}
				onRestoreResolution={onRestoreResolution}
				restoringAttendeeId={restoringAttendeeId}
			/>
		)
	}

	return (
		<TableScrollBox>
			{(scrollParent) => (
				<DataTable
					columnPinning={{ left: [], right: ['actions'] }}
					// Pinned right, as on the check-in and export tables: the
					// roster scrolls sideways well past the viewport, and an
					// action that scrolls away is one the operator cannot find.
					columns={columns}
					customScrollParent={scrollParent}
					data={data}
					isError={isError}
					isLoading={isLoading}
				/>
			)}
		</TableScrollBox>
	)
}

const CARD_CLASS =
	'flex flex-col gap-3 rounded-2xl bg-neutral-white p-4 shadow-airbnb-3'

const MOBILE_SKELETON_KEYS = ['m1', 'm2', 'm3', 'm4', 'm5']

interface AttendeesCardsProps {
	data: MarqueeAttendee[]
	eventId?: string
	captureMode?: MarqueeCaptureMode
	isRsvpEditable: boolean
	canResolve: boolean
	onAssignHost?: (attendee: MarqueeAttendee) => void
	onMarkUnresolvable?: (attendee: MarqueeAttendee) => void
	onRestoreResolution?: (attendee: MarqueeAttendee) => void
	restoringAttendeeId?: string | null
	isLoading?: boolean
	isError?: boolean
}

function AttendeesCards({
	data,
	eventId,
	captureMode,
	isRsvpEditable,
	canResolve,
	onAssignHost,
	onMarkUnresolvable,
	onRestoreResolution,
	restoringAttendeeId,
	isLoading,
	isError
}: AttendeesCardsProps) {
	if (isLoading) {
		return (
			<div className='flex flex-col gap-4'>
				{MOBILE_SKELETON_KEYS.map((key) => (
					<div className={CARD_CLASS} key={key}>
						<div className='flex items-start justify-between gap-3'>
							<Skeleton height={18} width={140} />
							<Skeleton height={24} width={90} />
						</div>
						<Skeleton height={16} width={200} />
						<Skeleton height={16} width={160} />
					</div>
				))}
			</div>
		)
	}

	if (isError) {
		return <TableError />
	}

	if (data.length === 0) {
		return <TableEmpty />
	}

	return (
		<div className='flex flex-col gap-4'>
			{data.map((attendee) => (
				<AttendeeCard
					attendee={attendee}
					canResolve={canResolve}
					captureMode={captureMode}
					eventId={eventId}
					isRestoring={restoringAttendeeId === attendee.id}
					isRsvpEditable={isRsvpEditable}
					key={attendee.id}
					onAssignHost={onAssignHost}
					onMarkUnresolvable={onMarkUnresolvable}
					onRestoreResolution={onRestoreResolution}
				/>
			))}
		</div>
	)
}

function AttendeeCard({
	attendee,
	eventId,
	captureMode,
	isRsvpEditable,
	canResolve,
	isRestoring,
	onAssignHost,
	onMarkUnresolvable,
	onRestoreResolution
}: {
	attendee: MarqueeAttendee
	eventId?: string
	captureMode?: MarqueeCaptureMode
	isRsvpEditable: boolean
	canResolve: boolean
	isRestoring: boolean
	onAssignHost?: (attendee: MarqueeAttendee) => void
	onMarkUnresolvable?: (attendee: MarqueeAttendee) => void
	onRestoreResolution?: (attendee: MarqueeAttendee) => void
}) {
	const { t } = useTranslation()
	const { pathname } = useLocation()
	const { value: childValue, isParent } = childAccountDisplay(attendee)
	const path = attendeeDetailPath(eventId, attendee.id, captureMode)

	return (
		<div className={CARD_CLASS}>
			{/* Name block and the row menu share the top line, the menu pinned to
			    the corner — same shape as the check-in card. */}
			<div className='flex items-start justify-between gap-2'>
				<div className='flex min-w-0 flex-col'>
					{path ? (
						<Link
							className='line-clamp-2 break-words font-medium text-neutral-900 text-sm underline underline-offset-2'
							state={{ from: pathname }}
							to={path}
						>
							{attendee.name || '-'}
						</Link>
					) : (
						<span className='line-clamp-2 break-words font-medium text-neutral-900 text-sm'>
							{attendee.name || '-'}
						</span>
					)}
					{attendee.company && (
						<span className='line-clamp-2 break-words text-neutral-500 text-sm'>
							{attendee.company}
						</span>
					)}
				</div>
				{/* Top-right of the card, opening a bottom sheet — the same
				    placement and surface the check-in roster uses on mobile. */}
				<AttendeeRowSheet
					attendee={attendee}
					canResolve={canResolve}
					detailPath={path}
					isRestoring={isRestoring}
					onAssignHost={onAssignHost}
					onMarkUnresolvable={onMarkUnresolvable}
					onRestoreResolution={onRestoreResolution}
				/>
			</div>

			<dl className='flex flex-col gap-1.5'>
				<CardRow label={t('marquee:attendee_col_rsvp')}>
					<RsvpStatusCell
						attendee={attendee}
						editable={isRsvpEditable}
						eventId={eventId}
					/>
				</CardRow>
				<CardRow label={t('marquee:attendee_col_child_account')}>
					{childValue ? (
						<span className='line-clamp-2 break-words'>
							<span className='font-medium text-neutral-900'>
								{childValue}
							</span>
							{isParent && (
								<span className='text-neutral-500'>
									{' · '}
									{t('marquee:attendee_child_parent')}
								</span>
							)}
						</span>
					) : (
						<span className='text-neutral-500'>
							{t(missingCellKey(attendee))}
						</span>
					)}
				</CardRow>
				<CardRow label={t('marquee:attendee_col_host_id')}>
					<span
						className={cn(
							'line-clamp-2 break-words',
							attendee.airbnb_host_id
								? 'font-medium text-neutral-900'
								: 'text-neutral-500'
						)}
					>
						{attendee.airbnb_host_id ?? t(missingCellKey(attendee))}
					</span>
				</CardRow>
				<CardRow label={t('marquee:attendee_col_segment')}>
					<span className='line-clamp-2 break-words font-medium text-neutral-900'>
						{segmentLabel(attendee.segment)}
					</span>
				</CardRow>
			</dl>
		</div>
	)
}

/**
 * The row menu for an attendee the export cannot place. Mirrors the check-in
 * tab's kebab so the two rosters offer the same shape, carrying the resolution
 * half of that matrix; the check-in writes belong to the desk and have no
 * meaning here.
 *
 * On a row still in play:
 * - **Resolve ID capture** opens the same assign-host dialog the check-in and
 *   export tabs use, so a row fixed here is fixed everywhere.
 * - **Mark unresolvable** is the give-up, offered because this is the surface
 *   the lock's warning sends the operator to.
 *
 * On a row already given up on, both are replaced by **Restore resolution**.
 * The mark is a judgement rather than a fact, so every surface that can make it
 * must also be able to walk it back: the check-in desk does, the export tab
 * does, and this roster used to be the one dead end, offering the give-up
 * again and nothing else.
 *
 * **See details** stays on both, when the row has a detail page (a tune-up).
 *
 * Renders nothing for a row that carries an account and no mark, or while the
 * event is `locked`, the one state where these writes answer 409
 * `invalid-state`.
 */
function AttendeeRowMenu({
	attendee,
	canResolve,
	detailPath,
	isRestoring,
	onAssignHost,
	onMarkUnresolvable,
	onRestoreResolution
}: {
	attendee: MarqueeAttendee
	canResolve: boolean
	detailPath?: string
	isRestoring?: boolean
	onAssignHost?: (attendee: MarqueeAttendee) => void
	onMarkUnresolvable?: (attendee: MarqueeAttendee) => void
	onRestoreResolution?: (attendee: MarqueeAttendee) => void
}) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { pathname } = useLocation()

	if (!(canResolve && hasRowActions(attendee))) {
		return null
	}

	const dispatch = (action: CheckInActionId) => {
		if (action === 'restore_resolution') {
			onRestoreResolution?.(attendee)
			return
		}
		if (action === 'mark_unresolvable') {
			onMarkUnresolvable?.(attendee)
			return
		}
		onAssignHost?.(attendee)
	}

	return (
		<DropdownMenuRoot>
			<DropdownMenuTrigger asChild>
				<button
					aria-label={t('marquee:checkin_row_menu_label')}
					className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition hover:bg-grey-100 hover:text-neutral-900 data-[state=open]:bg-grey-100 data-[state=open]:text-neutral-900'
					type='button'
				>
					<DotsHorizontalIcon className='rotate-90' size={20} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownContent align='end' className='w-56' sideOffset={4}>
					{rowResolutionActions(attendee).map((action) => (
						<DropdownItem
							className={cn(
								'flex items-center gap-2',
								ACTION_TONE_CLASS[action.tone]
							)}
							disabled={isRestoring}
							key={action.id}
							onSelect={() => dispatch(action.id)}
						>
							{t(checkInActionToLabelKey[action.id])}
							{isRestoring && <Spinner size={14} />}
						</DropdownItem>
					))}
					{detailPath && (
						<DropdownItem
							className='text-neutral-900'
							// Same `state.from` the name link records, so the
							// detail page's back button returns to this tab.
							onClick={() =>
								navigate(detailPath, {
									state: { from: pathname }
								})
							}
						>
							{t('marquee:checkin_action_see_details')}
						</DropdownItem>
					)}
				</DropdownContent>
			</DropdownMenuPortal>
		</DropdownMenuRoot>
	)
}

/**
 * Mobile counterpart of [[AttendeeRowMenu]]: the same actions for the same rows,
 * reached from a kebab in the card's top-right corner and listed in a bottom
 * sheet, the surface the check-in roster already uses on mobile, reused rather
 * than restyled.
 *
 * The actions carry the check-in matrix's own ids, so the sheet labels and tones
 * them exactly as it does there and the dispatcher below is a three-case switch.
 *
 * The two fixes each open a dialog that owns its own wait, so the sheet is
 * dismissed on tap and has nothing to spin on. The restore is the exception: it
 * writes from here, so the sheet stays up and reports the wait on the button the
 * operator tapped, as the check-in sheet does for its own writes.
 */
function AttendeeRowSheet({
	attendee,
	canResolve,
	detailPath,
	isRestoring,
	onAssignHost,
	onMarkUnresolvable,
	onRestoreResolution
}: {
	attendee: MarqueeAttendee
	canResolve: boolean
	detailPath?: string
	isRestoring?: boolean
	onAssignHost?: (attendee: MarqueeAttendee) => void
	onMarkUnresolvable?: (attendee: MarqueeAttendee) => void
	onRestoreResolution?: (attendee: MarqueeAttendee) => void
}) {
	const [open, setOpen] = useState(false)

	if (!(canResolve && hasRowActions(attendee))) {
		return null
	}

	return (
		<CheckInRowSheet
			actions={rowResolutionActions(attendee)}
			attendee={attendee}
			detailPath={detailPath}
			onAction={(row, action) => {
				if (action === 'restore_resolution') {
					onRestoreResolution?.(row)
					return
				}
				setOpen(false)
				if (action === 'mark_unresolvable') {
					onMarkUnresolvable?.(row)
					return
				}
				onAssignHost?.(row)
			}}
			onOpenChange={setOpen}
			open={open}
			pending={isRestoring ? 'restore_resolution' : null}
		/>
	)
}

function CardRow({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className='flex items-center justify-between gap-4 text-sm'>
			<dt className='shrink-0 text-neutral-500'>{label}</dt>
			<dd className='flex min-w-0 justify-end break-words text-right'>
				{children}
			</dd>
		</div>
	)
}
